import { computed, useContext } from "@nuxtjs/composition-api";
import { sharedRef, useVSFContext, Logger } from "@vue-storefront/core";
import { COOKIES } from "~/helpers/constants";
import { useSapcc } from "~/composables";
import { userCheckoutStore } from "~/stores/checkout-store";
import {getCMSLabels} from "~/helpers/globalLabels";

const useCheckout = (id: string) => {
  const genericError = getCMSLabels('genericerror');
  // Loads context used to call API endpoint
  const context = useVSFContext();
  const { error: nuxtError, i18n } = useContext();
  // Shared ref holding the response from the API
  const result = sharedRef(null, `checkout-${id}`);

  // Shared ref indicating whether any method is waiting for the data from the API
  const loading = sharedRef(false, `checkout-loading-${id}`);

  //store user id or Anonymous
  const userID = sharedRef(false, `checkout-userid-${id}`);
  const guestUserID = sharedRef(false, `checkout-guest-userid-${id}`);

  const isAuthenticated = computed( () => context.$cookies.get(COOKIES.USER) === COOKIES.CURRENT_USER_ID && !!context.$cookies.get(COOKIES.USER_DATA));

  // Shared ref holding errors from the methods`
  const error = sharedRef(null, `checkout-error-${id}`);

  //Load sapcc token
  const { load: loadSapccToken, result: sapccTokenValue } =
    useSapcc("generateSapccToken");

  const useCheckoutStore = userCheckoutStore();
  const baseStoreId = i18n.localeProperties.baseStoreId;

  const getUserID = () => {
    const useCookieData = context.$cookies.get(COOKIES.USER_DATA);
    return useCookieData ? useCookieData.u : "anonymous";
  };

  // get delivery modes from the sapcc
  const getDeliveryModes = async (params: { cartId: String }) => {
    try {
      loading.value = true;
      const userUID = await getUserID();

      await loadSapccToken();

      if (sapccTokenValue.value == "") {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const deliverymodesRes =
          await context.$vsf.$sapccvolatile.api.getDeliveryModes({
            userId: userUID,
            token: sapccTokenValue.value,
            cartId: params.cartId,
            baseStoreId,
          });
        if (deliverymodesRes?.errors) {
          //access token error
          error.value =
            deliverymodesRes.errors.length > 0
              ? deliverymodesRes.errors[0]
              : deliverymodesRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          result.value = deliverymodesRes?.deliveryModes;
        }
      }
    } catch (err) {
      error.value = err;
      Logger.error(`getDeliveryModes Error ==>>  ${error.value}`);
      return { errorMessage: error.value };
    } finally {
      loading.value = false;
    }
  };

  // GET Delivery Mode for Cart For Home Delivery after user address input
  const getDeliveryModeForCart = async (params: { cartId: String }) => {
    console.log('get delivery')
    try {
      loading.value = true;
      const userUID = await getUserID();

      await loadSapccToken();

      if (sapccTokenValue.value == "") {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const deliverymodesRes =
          await context.$vsf.$sapccvolatile.api.getDeliveryModeForCart({
            userId: userUID,
            token: sapccTokenValue.value,
            cartId: params.cartId,
            baseStoreId,
          });

        if (deliverymodesRes?.errors) {
          //access token error
          error.value =
            deliverymodesRes.errors.length > 0
              ? deliverymodesRes.errors[0]
              : deliverymodesRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          result.value = deliverymodesRes;
        }
      }
    } catch (err) {
      error.value = err;
      Logger.error(`getDeliveryModeForCart Error ==>>  ${error.value}`);
      return { errorMessage: error.value };
    } finally {
      loading.value = false;
    }
  };

  // remove delivery mode
  const removePrefferedDeliveryMode = async (params: {
    cartId: String;
  }) => {
    try {
      loading.value = true;

      const userUID = await getUserID();

      await loadSapccToken();

      if (sapccTokenValue.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const deliverymodesRes =
          await context.$vsf.$sapccvolatile.api.removePrefferedDeliveryMode({
            userId: userUID,
            token: sapccTokenValue.value,
            cartId: params.cartId,
            baseStoreId,
          });

        if (deliverymodesRes?.errors) {
          //access token error
          error.value =
            deliverymodesRes.errors.length > 0
              ? deliverymodesRes.errors[0]
              : deliverymodesRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          error.value = null;
          result.value = {
            status: 200,
            message: "removed preffered delivery mode successfully!",
          };
        }
      }
    } catch (err) {
      error.value = err;
      Logger.error(`removePrefferedDeliveryMode Error ==>>  ${error.value}`);
      return { errorMessage: error.value };
    } finally {
      loading.value = false;
    }
  };

  // set delivery mode
  const setDeliveryMode = async (params: {
    cartId: String;
    deliveryModeId: String;
    deliveryTypeCode: String;
  }) => {
    try {
      loading.value = true;

      const userUID = await getUserID();

      await loadSapccToken();

      if (sapccTokenValue.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
      const deliverymodesRes =
        await context.$vsf.$sapccvolatile.api.setDeliveryMode({
          userId: userUID,
          token: sapccTokenValue.value,
          cartId: params.cartId,
          deliveryModeId: params.deliveryModeId,
          deliveryTypeCode: params.deliveryTypeCode,
          baseStoreId,
        });

        if (deliverymodesRes?.errors) {
          //access token error
          error.value =
            deliverymodesRes.errors.length > 0
              ? deliverymodesRes.errors[0]
              : deliverymodesRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          error.value = null;
          result.value = {
            status: 200,
            message: "Set delivery mode successfully!",
          };
        }
      }
    } catch (err) {
      error.value = err;
      Logger.error(`getDeliveryModes Error ==>>  ${error.value}`);
      return { errorMessage: error.value };
    } finally {
      loading.value = false;
    }
  };

  // set delivery address for cart
  const setDeliveryAddressForCart = async (params: {
    cartId: String;
    countryCode: String;
    email?: String;
    companyName?: String;
    firstName?: String;
    lastName?: String;
    line1?: String;
    line2?: String;
    phone?: String;
    postalCode?: String;
    town?: String;
    addressValidation?: Boolean;
    storePickUpId?: String;
    baseStoreId?: String;
    isInternationalUpdate?: Boolean;
  }) => {
    try {
      loading.value = true;

      const userUID = await getUserID();

      await loadSapccToken();

      if (sapccTokenValue.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const deliveryAddressResp =
          await context.$vsf.$sapccvolatile.api.setDeliveryAddress({
            userId: userUID,
            token: sapccTokenValue.value,
            cartId: params.cartId,
            countryCode: params.countryCode,
            ...(!params.isInternationalUpdate && {
              email: params.email,
              companyName: params.companyName,
              firstName: params.firstName,
              lastName: params.lastName,
              line1: params.line1,
              line2: params.line2,
              phone: params.phone,
              postalCode: params.postalCode,
              town: params.town,
              storePickUpId: params.storePickUpId || "",
            }),
            addressValidation: params.addressValidation ?? true,
            baseStoreId,
          });

        if (deliveryAddressResp?.errors) {
          //access token error
          error.value =
            deliveryAddressResp.errors.length > 0
              ? deliveryAddressResp.errors[0]
              : deliveryAddressResp.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          error.value = null;
          result.value = {
            status: 200,
            message: "Address added successfully!",
          };
        }
      }
    } catch (err) {
      error.value = err;
      Logger.error(`getDeliveryModes Error ==>>  ${error.value}`);
      return { errorMessage: error.value };
    } finally {
      loading.value = false;
    }
  };

  /**
   * update cart when changing international country
   * because we are using same store and sapcc needs to know
   * for which country we need shipping methods
  */
  const updateInternationalDeliveryAddressForCart = async (params: {
    cartId: String;
    countryCode: String;
  }) => {
    return setDeliveryAddressForCart({
      ...params,
      isInternationalUpdate: true,
      addressValidation: true
    });
  };

  // Set Billing address of the customer
  const setCustomerBillingAddress = async (params: {
    cartId: string;
    countryCode: string;
    country: string;
    email: string;
    companyName: string;
    firstName: string;
    lastName: string;
    line1: string;
    line2: string;
    phone: string;
    postalCode: string;
    town: string;
    baseStoreId?: string;
  }) => {
    try {
      loading.value = true;

      const userUID = await getUserID();

      await loadSapccToken();

      if (sapccTokenValue.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const addBillingResponse =
          await context.$vsf.$sapccvolatile.api.setCustomerBillingAddress({
            userId: userUID,
            token: sapccTokenValue.value,
            cartId: params.cartId,
            countryCode: params.countryCode,
            email: params.email,
            companyName: params.companyName,
            firstName: params.firstName,
            lastName: params.lastName,
            line1: params.line1,
            line2: params.line2,
            phone: params.phone,
            postalCode: params.postalCode,
            town: params.town,
            baseStoreId,
          });

        if (addBillingResponse?.errors) {
          //access token error
          error.value =
            addBillingResponse.errors.length > 0
              ? addBillingResponse.errors[0]
              : addBillingResponse.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          error.value = null;
          result.value = {
            status: 200,
            message: "Payment address added successfully!",
          };
        }
      }
    } catch (err) {
      error.value = err;
      Logger.error(`setCustomerBillingAddress Error ==>>  ${error.value}`);
      return { errorMessage: error.value };
    } finally {
      loading.value = false;
    }
  };

  //save customer information after billing shipping addresses are added
  const saveCustomerInformation = async (params: {
    cartId: string;
    contact: {
      email: string;
    };
    delivery: {
      shippingTypeId: string;
      qasSuggestion: string;
      shipping: {
        addressId: string;
        title: string;
        selectedShippingType: string;
        firstName: string;
        lastName: string;
        street: string;
        addressDetails: string;
        postal: string;
        city: string;
        country: string;
        state: string;
        phone: string;
        countryCode: string;
        saveAddress: Boolean;
        baseStoreId?: string;
      };
    };
  }) => {
    try {
      loading.value = true;
      const userUID = await getUserID();

      await loadSapccToken();

      if (sapccTokenValue.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const addressResponse =
          await context.$vsf.$sapccvolatile.api.saveCustomerInfo({
            userId: userUID,
            token: sapccTokenValue.value,
            cartId: params.cartId,
            body: params,
            baseStoreId,
          });

        if (addressResponse?.errors) {
          //access token error
          error.value =
            addressResponse.errors.length > 0
              ? addressResponse.errors[0]
              : addressResponse.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          error.value = null;
          result.value = {
            status: 200,
            message: "Set address successfully!",
          };
        }
      }
    } catch (err) {
      error.value = err;
      Logger.error(`setCustomerBillingAddress Error ==>>  ${error.value}`);
      return { errorMessage: error.value };
    } finally {
      loading.value = false;
    }
  };

  const prepareForCheckout = async (params: { cartId: string }) => {
    try {
      loading.value = true;

      const userUID = await getUserID();

      await loadSapccToken();

      if (sapccTokenValue.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const prepareCheckoutRes =
          await context.$vsf.$sapccvolatile.api.prepareForCheckout({
            userId: userUID,
            token: sapccTokenValue.value,
            cartId: params.cartId,
            baseStoreId,
          });

        if (prepareCheckoutRes?.errors) {
          //access token error
          error.value =
            prepareCheckoutRes.errors.length > 0
              ? prepareCheckoutRes.errors[0]
              : prepareCheckoutRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          error.value = null;
          result.value = {
            status: 200,
            message: "Payment address added successfully!",
            finalCart: prepareCheckoutRes,
          };
        }
      }
    } catch (err) {
      error.value = err;
      Logger.error(`Prepare for checkout Error ==>>  ${error.value}`);
      return { errorMessage: error.value };
    } finally {
      loading.value = false;
    }
  };

  const guestLogin = async (params: { cartId: string; email: string }) => {
    try {
      loading.value = true;

      const userUID = await getUserID();

      await loadSapccToken();

      if (sapccTokenValue.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const prepareCheckoutRes =
          await context.$vsf.$sapccvolatile.api.guestLogin({
            userId: userUID,
            token: sapccTokenValue.value,
            cartId: params.cartId,
            baseStoreId,
          });
        if (prepareCheckoutRes?.errors) {
          //access token error
          error.value =
            prepareCheckoutRes.errors.length > 0
              ? prepareCheckoutRes.errors[0]
              : prepareCheckoutRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          const guestId: string = prepareCheckoutRes?.uid || "";
          error.value = null;
          result.value = {
            status: 200,
            uid: guestId,
          };
          guestUserID.value = guestId;
          useCheckoutStore.setGuestUser({
            email: params.email,
            uid: guestId,
          });
        }
      }
    } catch (err) {
      error.value = err;
      Logger.error(`Guest user login Error ==>>  ${error.value}`);
      return { errorMessage: error.value };
    } finally {
      loading.value = false;
    }
  };

  return {
    isAuthenticated,
    getUserID,
    getDeliveryModes,
    getDeliveryModeForCart,
    removePrefferedDeliveryMode,
    setDeliveryMode,
    setDeliveryAddressForCart,
    setCustomerBillingAddress,
    saveCustomerInformation,
    prepareForCheckout,
    guestLogin,
    updateInternationalDeliveryAddressForCart,
    guestUserID: computed(() => guestUserID.value),
    result: computed(() => result.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  };
};
export default useCheckout;
